'use client';

import { Control as RadixFormControl } from '@radix-ui/react-form';
import { useState, useRef, forwardRef, useEffect } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import type { ChangeEvent, InputHTMLAttributes } from 'react';
import type React from 'react';

/**
 * Defines the properties for the custom input attributes by omitting certain properties from the standard HTML input attributes.
 */
type InputAttributes = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'onChange' | 'defaultValue'
>;

/**
 * Provides details for formatting date inputs like day, month and year.
 */
interface DateFormatDetails {
  maxDecimalPlaces: number;
  placeholder: string;
  label: string;
}

/**
 * Represents the different date formats based on locale.
 */
interface Format {
  [key: string]: DateFormatDetails | undefined;
}

/**
 * Props for the BirthdateField component, with customization options for className, locale handling, value changes, and defaults.
 */
export type BirthdateFieldProps = InputAttributes & {
  className?: string;
  locale?: string;
  onChange?: (value: string) => void;
  defaultValue?: string; // YYYY-MM-JJ
  readOnly?: boolean;
  disabled?: boolean;
  /** Minimum allowed year. Defaults to currentYear - 100 (birthday mode). */
  minYear?: number;
  /** Maximum allowed year. Defaults to currentYear (birthday mode). */
  maxYear?: number;
};

type SetPartFunction = {
  day: React.Dispatch<React.SetStateAction<string>>;
  month: React.Dispatch<React.SetStateAction<string>>;
  year: React.Dispatch<React.SetStateAction<string>>;
};

type PartKey = keyof SetPartFunction;

/**
 * Returns the date format details based on the provided locale.
 * @param {string} locale - The locale code to determine the date format.
 * @returns {Format} The format details for the given locale.
 */
const getFormatBasedOnLocale = (locale: string = 'de-DE'): Format => {
  switch (locale) {
    case 'en':
    case 'en-US':
    case 'en-GB':
      return {
        day: { maxDecimalPlaces: 2, placeholder: 'DD', label: 'Day' },
        month: { maxDecimalPlaces: 2, placeholder: 'MM', label: 'Month' },
        year: { maxDecimalPlaces: 4, placeholder: 'YYYY', label: 'Year' },
      };
    default:
      return {
        day: { maxDecimalPlaces: 2, placeholder: 'TT', label: 'Tag' },
        month: { maxDecimalPlaces: 2, placeholder: 'MM', label: 'Monat' },
        year: { maxDecimalPlaces: 4, placeholder: 'JJJJ', label: 'Jahr' },
      };
  }
};

/**
 * Combines individual date components into a single date string in the format YYYY-MM-DD.
 * @param {string} day - Day part of the date.
 * @param {string} month - Month part of the date.
 * @param {string} year - Year part of the date.
 * @returns {string} The combined date string.
 */
const createCombinedValue = (day: string, month: string, year: string) => {
  return day && month && year
    ? `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    : '';
};

/**
 * Parses an initial date value into its component parts.
 * @param {string} value - The initial date value in the format YYYY-MM-DD.
 * @returns {object} An object containing day, month and year as strings.
 */
const parseInitialValue = (value: string) => {
  // Strip time portion from ISO 8601 strings (e.g. "2020-01-05T00:00:00Z" → "2020-01-05")
  const dateOnly = value.split('T')[0];
  const parts = dateOnly.split('-');
  return {
    year: normalizeInitialValueSubPart(parts[0] ?? ''),
    month: normalizeInitialValueSubPart(parts[1] ?? ''),
    day: normalizeInitialValueSubPart(parts[2] ?? ''),
  };
};

/**
 * Normalizes a part of the initial value, ensuring it is a valid number.
 * @param {string} value - The value to normalize.
 * @returns {string} The normalized value, or an empty string if invalid.
 */
const normalizeInitialValueSubPart = (value: string) => {
  if (value === '') return value;
  const asNumber = parseInt(value, 10);
  return asNumber < 1 || Number.isNaN(asNumber) ? '' : String(asNumber);
};

/**
 * A component for inputting birthdates with locale-based formatting.
 *
 * ### Configuration
 * - Default locale *static*: `de-DE`
 * - Input *defaultValue* format: `YYYY-MM-DD`
 * - Output format: `YYYY-MM-DD`
 * - Oldest passenger age: `100`
 */
export const BirthdateField = forwardRef<HTMLInputElement, BirthdateFieldProps>(
  (
    {
      name,
      className,
      locale = 'de-DE',
      onChange,
      value,
      defaultValue,
      readOnly,
      disabled,
      minYear,
      maxYear,
      ...props
    },
    ref,
  ) => {
    const format = getFormatBasedOnLocale(locale);
    const seedValue = (value ?? defaultValue)?.toString() ?? '';
    const initials = parseInitialValue(seedValue);
    const [day, setDay] = useState(initials.day);
    const [month, setMonth] = useState(initials.month);
    const [year, setYear] = useState(initials.year);
    const [combinedValue, setCombinedValue] = useState(
      createCombinedValue(initials.day, initials.month, initials.year),
    );

    const prevValue = useRef(seedValue);
    const dayRef = useRef<HTMLInputElement>(null);
    const monthRef = useRef<HTMLInputElement>(null);
    const yearRef = useRef<HTMLInputElement>(null);

    const inputClassNames = twMerge(
      'w-full rounded-md border border-stroke-secondary-40 bg-surface-white p-4 text-center text-sm placeholder-secondary-marine-48 outline-none',
      'text-marine-high-emphasis focus:outline-none focus-visible:shadow-focus-state data-[invalid]:border-stroke-error-100',
      // Match the standard InputField disabled/read-only "greyed out" style.
      'disabled:border-transparent disabled:bg-surface-secondary-7 disabled:text-marine-medium-emphasis',
      'read-only:border-transparent read-only:bg-surface-secondary-7 read-only:text-marine-medium-emphasis',
    );

    useEffect(() => {
      const newCombinedValue = createCombinedValue(day, month, year);
      setCombinedValue(newCombinedValue);
      if (newCombinedValue !== prevValue.current && onChange) {
        prevValue.current = newCombinedValue;
        onChange(newCombinedValue);
      }
    }, [day, month, year, onChange]);

    const handleChange = (
      part: PartKey,
      event: ChangeEvent<HTMLInputElement>,
      nextRef?: React.RefObject<HTMLInputElement | null>,
    ) => {
      if (readOnly || disabled) return;

      const setPart: SetPartFunction = {
        day: setDay,
        month: setMonth,
        year: setYear,
      } as const;
      const formatPart = format[part];

      if (!formatPart) return;

      const value = event.target.value.slice(0, formatPart.maxDecimalPlaces);

      setPart[part](value);

      // Auto-advance only when the user is **typing or pasting**: native
      // number inputs also fire `onChange` for the spinner / ↑↓ keys, and
      // browsers disagree on what `inputType` to set for that:
      //   - Chrome: `''` (empty) for spinner-clicks.
      //   - Firefox: `'insertReplacementText'` for spinner-clicks.
      // A `startsWith('insert')` check would let Firefox's spinner through;
      // instead, whitelist the two explicit user-text-edit types.
      const inputType = (event.nativeEvent as InputEvent).inputType;
      const isTypedEdit =
        inputType === 'insertText' || inputType === 'insertFromPaste';
      if (
        isTypedEdit &&
        value.length === formatPart.maxDecimalPlaces &&
        nextRef?.current
      ) {
        nextRef.current.focus();
      }
    };

    // Simple function to generate a "unique" ID by appending a random number
    const uniqueId = (base: string) =>
      `${base}-${Math.floor(Math.random() * 1000)}`;

    const inputs = [
      { part: 'day', ref: dayRef, nextRef: monthRef },
      { part: 'month', ref: monthRef, nextRef: yearRef },
      { part: 'year', ref: yearRef, nextRef: undefined },
    ].map(({ part, ref, nextRef }) => {
      const formatDetail = format[part];
      const id = uniqueId(`birthdate-${part}`);
      if (!formatDetail) return null;
      return (
        <div key={part}>
          <input
            id={id}
            {...props}
            ref={ref}
            type="number"
            disabled={disabled}
            readOnly={readOnly}
            maxLength={formatDetail.maxDecimalPlaces}
            placeholder={formatDetail.placeholder}
            className={inputClassNames}
            value={part === 'day' ? day : part === 'month' ? month : year}
            onChange={event =>
              handleChange(part as keyof SetPartFunction, event, nextRef)
            }
            min={
              part === 'year' ? (minYear ?? new Date().getFullYear() - 100) : 1
            }
            max={
              part === 'year'
                ? (maxYear ?? new Date().getFullYear())
                : part === 'month'
                  ? 12
                  : 31
            }
          />
          <label
            htmlFor={id}
            className="mt-1 inline-block cursor-pointer text-sm text-marine-medium-emphasis"
          >
            {formatDetail.label}
          </label>
        </div>
      );
    });

    return (
      <>
        <RadixFormControl asChild>
          <input
            {...props}
            ref={ref}
            type="hidden"
            name={name}
            value={combinedValue}
          />
        </RadixFormControl>
        <div className={twJoin('grid grid-cols-3 gap-3', className)}>
          {inputs}
        </div>
      </>
    );
  },
);
